import React, { useEffect, useRef, useCallback } from "react";
import "./common.scss";
const AttentionModal = React.memo(
  ({
    isOpen,
    onClose,
    /** Primary action (e.g. OK) — user accepts the choice. */
    onConfirm,
    /** Backdrop, Escape, secondary button — user backs out without confirming. */
    onDismiss,
    btnTitle = "Close",
    children,
    type = "success",
    bottomBtn,
  }) => {
    const handleDismiss = onDismiss ?? onClose;
    const handleConfirm = onConfirm ?? onClose;
    const modalRef = useRef(null);
    const previousFocusRef = useRef(null);

    // Trap focus inside the modal
    const handleKeyDown = useCallback(
      (e) => {
        if (e.key === "Escape") {
          onClose();
          return;
        }
        if (e.key !== "Tab") return;

        const modal = modalRef.current;
        if (!modal) return;
        const focusable = modal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      },
      [handleDismiss],
    );

    // Focus management: save previous focus, auto-focus modal, restore on close
    useEffect(() => {
      if (isOpen) {
        previousFocusRef.current = document.activeElement;
        // Auto-focus first focusable element inside modal after render
        requestAnimationFrame(() => {
          const modal = modalRef.current;
          if (!modal) return;
          const first = modal.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          );
          if (first) first.focus();
        });
      } else if (previousFocusRef.current) {
        previousFocusRef.current.focus();
        previousFocusRef.current = null;
      }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
      <div className="modal-backdrop attention-modal" onClick={handleDismiss}>
        <div
          ref={modalRef}
          className={`modal-container ${type}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
        >
          <div className="modal-contents">{children}</div>
          <div className="modal-footer">
            <button
              type="button"
              className=" btn primary-button regular"
              onClick={handleConfirm}
            >
              {btnTitle}
            </button>
            {bottomBtn && (
              <button
                type="button"
                className=" btn secondary-button regular"
                onClick={handleDismiss}
              >
                {bottomBtn}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  },
);

AttentionModal.displayName = "AttentionModal";

export default AttentionModal;
